import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './DashboardOverview.css';
import { supabase } from '../../lib/supabase';

const DashboardOverview = ({ dataLoading, schoolsCount, usersCount, providersCount, refreshActivities }) => {
  const navigate = useNavigate();
  const [activities, setActivities] = useState([]);
  const [activitiesLoading, setActivitiesLoading] = useState(true);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [showDeleteItemConfirm, setShowDeleteItemConfirm] = useState(false);
  const [ads, setAds] = useState([]);
  const [adsLoading, setAdsLoading] = useState(true);
  const [showAddAdModal, setShowAddAdModal] = useState(false);
  const [showEditAdModal, setShowEditAdModal] = useState(false);
  const [newAd, setNewAd] = useState({ description: '', action_url: '', title: '', subtitle: '', button_text: '' });
  const [editingAd, setEditingAd] = useState(null);
  const [uploadingAd, setUploadingAd] = useState(false);
  const [adsEnabled, setAdsEnabled] = useState(true);
  const [adsEnabledLoading, setAdsEnabledLoading] = useState(true);
  const [showDeleteAdModal, setShowDeleteAdModal] = useState(false);
  const [deleteAdId, setDeleteAdId] = useState(null);
  const [actionUrlError, setActionUrlError] = useState('');
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [pendingCloseAction, setPendingCloseAction] = useState(null);
  const [initialAdFormData, setInitialAdFormData] = useState(null);

  // Fetch activities and ads from database
  useEffect(() => {
    fetchActivities();
    fetchAds();
    fetchAdsEnabled();
  }, []);

  // Refresh activities when refreshActivities prop changes
  useEffect(() => {
    if (refreshActivities) {
      fetchActivities();
    }
  }, [refreshActivities]);

  // Clear initial form data when modals close
  useEffect(() => {
    if (!showAddAdModal && !showEditAdModal) {
      setInitialAdFormData(null);
    }
  }, [showAddAdModal, showEditAdModal]);

  const fetchActivities = async () => {
    try {
      setActivitiesLoading(true);
      // Fetch only the last 5 admin activities for dashboard preview
      const { data, error } = await supabase
        .from('cms_activities')
        .select('*')
        .is('school_id', null)
        .order('added_at', { ascending: false })
        .limit(5);

      if (error) {} else {
        setActivities(data || []);
      }
    } catch (err) {
      } finally {
      setActivitiesLoading(false);
    }
  };


  const fetchAds = async () => {
    try {
      setAdsLoading(true);
      const { data, error } = await supabase
        .from('ads')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        setAds([]);
      } else {
        setAds(data || []);
      }
    } catch (err) {
      setAds([]);
    } finally {
      setAdsLoading(false);
    }
  };

  const fetchAdsEnabled = async () => {
    try {
      setAdsEnabledLoading(true);
      const { data, error } = await supabase
        .from('system_settings')
        .select('ads_enabled')
        .eq('id', 1)
        .single();

      if (error && error.code !== 'PGRST116') {// Default to true if error
        setAdsEnabled(true);
      } else {
        // Default to true if not set
        setAdsEnabled(data?.ads_enabled !== false);
      }
    } catch (err) {setAdsEnabled(true); // Default to true on error
    } finally {
      setAdsEnabledLoading(false);
    }
  };

  // Check if ad form has been modified
  const hasAdFormChanges = () => {
    if (!initialAdFormData) return false;
    return JSON.stringify(newAd) !== JSON.stringify(initialAdFormData);
  };

  // Handle overlay click with unsaved changes check
  const handleAdOverlayClick = (closeAction) => {
    if (hasAdFormChanges()) {
      setPendingCloseAction(() => closeAction);
      setShowDiscardConfirm(true);
    } else {
      closeAction();
    }
  };

  // Confirm discard changes
  const confirmDiscard = () => {
    if (pendingCloseAction) {
      pendingCloseAction();
      setPendingCloseAction(null);
    }
    setShowDiscardConfirm(false);
    setInitialAdFormData(null);
  };

  // Cancel discard
  const cancelDiscard = () => {
    setShowDiscardConfirm(false);
    setPendingCloseAction(null);
  };

  const handleToggleAdsEnabled = async (enabled) => {
    try {
      setAdsEnabled(enabled);

      // Update in database
      const { error } = await supabase
        .from('system_settings')
        .upsert({
          id: 1,
          ads_enabled: enabled
        }, {
          onConflict: 'id'
        });

      if (error) {// Revert on error
        setAdsEnabled(!enabled);
        alert('Error updating ads setting: ' + error.message);
      } else {}
    } catch (err) {// Revert on error
      setAdsEnabled(!enabled);
      alert('Error updating ads setting. Please try again.');
    }
  };

  const handleDeleteActivity = async (activityId) => {
    try {
      // Optimistically remove from UI
      setActivities(prev => prev.filter(activity => activity.id !== activityId));

      const { error } = await supabase
        .from('cms_activities')
        .delete()
        .eq('id', activityId);

      if (error) {
        await fetchActivities();
        alert('Error deleting activity: ' + error.message);
      } else {
        // Success - activities already updated optimistically
      }

      setShowDeleteItemConfirm(false);
      setDeleteItemId(null);
    } catch (err) {
      await fetchActivities();
      alert('Error deleting activity: ' + err.message);
      setShowDeleteItemConfirm(false);
      setDeleteItemId(null);
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validation: if button_text is provided, action_url is required
    if (newAd.button_text && newAd.button_text.trim() && (!newAd.action_url || !newAd.action_url.trim())) {
      setActionUrlError('Action URL is required when a button text is provided');
      return;
    }
    setActionUrlError(''); // Clear any previous errors
    try {
      setUploadingAd(true);
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
      const filePath = `ads/${fileName}`;// Upload to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('assets')
        .upload(filePath, file);

      if (uploadError) {
        alert('Error uploading image: ' + uploadError.message);
        return;
      }
      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('assets')
        .getPublicUrl(filePath);// Save to ads table
      const { error: insertError } = await supabase
        .from('ads')
        .insert({
          image_url: publicUrl,
          action_url: newAd.action_url,
          description: newAd.description,
          title: newAd.title || null,
          subtitle: newAd.subtitle || null,
          button_text: newAd.button_text || null,
          click_count: 0
        });

      if (insertError) {alert('Error saving ad: ' + insertError.message);
      } else {// Reset form and refresh ads
        setNewAd({ description: '', action_url: '', title: '', subtitle: '', button_text: '' });
        setActionUrlError(''); // Clear any errors
        setInitialAdFormData(null);
        setShowAddAdModal(false);
        fetchAds();

        // Add activity (admin activity - no school_id)
        await supabase
          .from('cms_activities')
          .insert({
            title: 'Ad Uploaded',
            details: `New ad uploaded: ${newAd.description || 'Untitled'}`,
            added_at: new Date().toISOString(),
            school_id: null
          });
      }
    } catch (err) {alert('Error uploading ad: ' + err.message);
    } finally {
      setUploadingAd(false);
    }
  };

  const handleEditAd = (ad) => {
    setEditingAd(ad);
    const formData = {
      description: ad.description || '',
      action_url: ad.action_url || '',
      title: ad.title || '',
      subtitle: ad.subtitle || '',
      button_text: ad.button_text || ''
    };
    setNewAd(formData);
    setInitialAdFormData(JSON.parse(JSON.stringify(formData)));
    setActionUrlError(''); // Clear any previous errors
    setShowEditAdModal(true);
  };

  const handleUpdateAd = async () => {
    if (!editingAd) return;

    // Validation: if button_text is provided, action_url is required
    if (newAd.button_text && newAd.button_text.trim() && (!newAd.action_url || !newAd.action_url.trim())) {
      setActionUrlError('Action URL is required when a button text is provided');
      return;
    }
    setActionUrlError(''); // Clear any previous errors

    try {
      setUploadingAd(true);
      const { error } = await supabase
        .from('ads')
        .update({
          description: newAd.description,
          action_url: newAd.action_url,
          title: newAd.title || null,
          subtitle: newAd.subtitle || null,
          button_text: newAd.button_text || null
        })
        .eq('id', editingAd.id);

      if (error) {alert('Error updating ad: ' + error.message);
      } else {setEditingAd(null);
        setNewAd({ description: '', action_url: '', title: '', subtitle: '', button_text: '' });
        setInitialAdFormData(null);
        setShowEditAdModal(false);
        fetchAds();

        // Add activity (admin activity - no school_id)
        await supabase
          .from('cms_activities')
          .insert({
            title: 'Ad Updated',
            details: `Ad updated: ${newAd.description || 'Untitled'}`,
            added_at: new Date().toISOString(),
            school_id: null
          });
      }
    } catch (err) {alert('Error updating ad: ' + err.message);
    } finally {
      setUploadingAd(false);
    }
  };

  const handleDeleteAd = async (adId) => {
    setDeleteAdId(adId);
    setShowDeleteAdModal(true);
  };

  const confirmDeleteAd = async () => {
    if (!deleteAdId) return;

    try {
      const { error } = await supabase
        .from('ads')
        .delete()
        .eq('id', deleteAdId);

      if (error) {alert('Error deleting ad: ' + error.message);
      } else {
        fetchAds();

        // Add activity (admin activity - no school_id)
        await supabase
          .from('cms_activities')
          .insert({
            title: 'Ad Deleted',
            details: 'An ad was deleted from the system',
            added_at: new Date().toISOString(),
            school_id: null
          });
      }
    } catch (err) {alert('Error deleting ad: ' + err.message);
    } finally {
      setShowDeleteAdModal(false);
      setDeleteAdId(null);
    }
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const activityTime = new Date(timestamp);
    const diffInSeconds = Math.floor((now - activityTime) / 1000);

    if (diffInSeconds < 60) {
      return 'Just now';
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    } else {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} day${days > 1 ? 's' : ''} ago`;
    }
  };

  const getActivityIcon = (title) => {
    if (title.toLowerCase().includes('school')) {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M19 13H13V19H11V13H5V11H11V5H13V11H19V13Z" />
        </svg>
      );
    } else if (title.toLowerCase().includes('user') || title.toLowerCase().includes('student')) {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z" />
        </svg>
      );
    } else if (title.toLowerCase().includes('provider')) {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM19 19H5V5H19V19ZM17 12H7V10H17V12ZM15 16H7V14H15V16ZM17 8H7V6H17V8Z" />
        </svg>
      );
    } else {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M19 13H13V19H11V13H5V11H11V5H13V11H19V13Z" />
        </svg>
      );
    }
  };

  return (
    <div className="overview-content">
      {/* Discard Changes Confirmation Dialog */}
      {showDiscardConfirm && (
        <div className="unsaved-changes-overlay" onClick={cancelDiscard}>
          <div className="unsaved-changes-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="unsaved-changes-header">
              <div className="unsaved-changes-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                </svg>
              </div>
              <h2>Unsaved Changes</h2>
            </div>
            <div className="unsaved-changes-body">
              <p className="unsaved-changes-message">
                You have unsaved changes. Are you sure you want to discard them?
              </p>
            </div>
            <div className="unsaved-changes-actions">
              <button className="unsaved-changes-cancel-btn" onClick={cancelDiscard}>
                Cancel
              </button>
              <button className="unsaved-changes-discard-btn" onClick={confirmDiscard}>
                Discard Changes
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="overview-stats-grid">
        <div className="overview-stat-card">
          <div className="overview-stat-info">
            <h3>Total Schools</h3>
            <div className="overview-stat-number">{dataLoading ? '...' : schoolsCount}</div>
          </div>
          <div className="overview-stat-icon overview-school-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 3L1 9L12 15L21 10.09V17H23V9M5 13.18V17.18L12 21L19 17.18V13.18L12 17L5 13.18Z" />
            </svg>
          </div>
        </div>

        <div className="overview-stat-card">
          <div className="overview-stat-info">
            <h3>Total Users</h3>
            <div className="overview-stat-number">{dataLoading ? '...' : usersCount}</div>
          </div>
          <div className="overview-stat-icon overview-student-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z" />
            </svg>
          </div>
        </div>

        <div className="overview-stat-card">
          <div className="overview-stat-info">
            <h3>Learning Providers</h3>
            <div className="overview-stat-number">{dataLoading ? '...' : providersCount}</div>
          </div>
          <div className="overview-stat-icon overview-provider-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM19 19H5V5H19V19ZM17 12H7V10H17V12ZM15 16H7V14H15V16ZM17 8H7V6H17V8Z" />
            </svg>
          </div>
        </div>
      </div>

      <div className="overview-recent-activity">
        <div className="overview-activity-header">
          <h2>Recent Activity</h2>
          <button
            className="overview-view-all-btn"
            onClick={() => navigate('/dashboard/activities')}
          >
            View All
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" />
            </svg>
          </button>
        </div>

        {activitiesLoading ? (
          <div className="overview-activity-loading">
            <div className="overview-activity-spinner">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2L13.09 8.26L22 9L13.09 9.74L12 16L10.91 9.74L2 9L10.91 8.26L12 2Z" />
              </svg>
            </div>
            <p>Loading activities...</p>
          </div>
        ) : activities.length === 0 ? (
          <div className="overview-no-activity">
            <div className="overview-no-activity-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
              </svg>
            </div>
            <h3>No Recent Activity</h3>
            <p>Activities will appear here when you add schools, users, or providers.</p>
          </div>
        ) : (
          <div className="overview-activity-list-container">
            <div className="overview-activity-list">
              {activities.map((activity) => (
                <div key={activity.id} className="overview-activity-item">
                  <div className="overview-activity-icon">
                    {getActivityIcon(activity.title)}
                  </div>
                  <div className="overview-activity-content">
                    <p><strong>{activity.title}</strong></p>
                    {activity.details && <p className="overview-activity-details">{activity.details}</p>}
                    <span className="overview-activity-time">{formatTimeAgo(activity.added_at)}</span>
                  </div>
                  <button
                    className="overview-activity-delete-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteItemId(activity.id);
                      setShowDeleteItemConfirm(true);
                    }}
                    title="Delete activity"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19 4H15.5L14.5 3H9.5L8.5 4H5V6H19M6 19C6 20.1 6.9 21 8 21H16C17.1 21 18 20.1 18 19V7H6V19Z" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Ads Management Section */}
      <div className="overview-ads-management">
        <div className="overview-ads-header">
          <h2>Advertisement Management</h2>
          <div className="overview-ads-header-actions">
            <div className="overview-ads-toggle-container">
              <label className="overview-ads-toggle-label">
                <span>Enable Ads</span>
                {adsEnabledLoading ? (
                  <div className="overview-ads-toggle-loading">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2L13.09 8.26L22 9L13.09 9.74L12 16L10.91 9.74L2 9L10.91 8.26L12 2Z" />
                    </svg>
                  </div>
                ) : (
                  <div
                    className={`overview-ads-toggle ${adsEnabled ? 'enabled' : 'disabled'}`}
                    onClick={() => handleToggleAdsEnabled(!adsEnabled)}
                  >
                    <div className="overview-ads-toggle-slider"></div>
                  </div>
                )}
              </label>
            </div>
            <button
              className="add-ad-btn"
              onClick={() => {
                setNewAd({ description: '', action_url: '', title: '', subtitle: '', button_text: '' });
                setInitialAdFormData({ description: '', action_url: '', title: '', subtitle: '', button_text: '' });
                setShowAddAdModal(true);
              }}
              disabled={!adsEnabled}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 13H13V19H11V13H5V11H11V5H13V11H19V13Z" />
              </svg>
              Add Ad
            </button>
          </div>
        </div>

        {adsLoading ? (
          <div className="overview-ads-loading">
            <div className="overview-ads-spinner">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2L13.09 8.26L22 9L13.09 9.74L12 16L10.91 9.74L2 9L10.91 8.26L12 2Z" />
              </svg>
            </div>
            <p>Loading ads...</p>
          </div>
        ) : ads.length === 0 ? (
          <div className="overview-no-ads">
            <div className="overview-no-ads-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM19 19H5V5H19V19ZM17 12H7V10H17V12ZM15 16H7V14H15V16ZM17 8H7V6H17V8Z" />
              </svg>
            </div>
            <h3>No Ads Available</h3>
            <p>Upload your first advertisement to get started.</p>
          </div>
        ) : (
          <div className="overview-ads-grid">
            {ads.map((ad) => (
              <div
                key={ad.id}
                className="overview-ad-card"
                onClick={() => handleEditAd(ad)}
              >
                <div className="overview-ad-image">
                  <img src={ad.image_url} alt={ad.description || 'Advertisement'} />
                </div>
                <div className="overview-ad-info">
                  <h4>{ad.description || 'Untitled Ad'}</h4>
                  <p className="overview-ad-url">{ad.action_url || 'No action URL'}</p>
                  <p className="overview-ad-stats">{ad.click_count || 0} clicks</p>
                </div>
                <button
                  className="overview-ad-delete-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteAd(ad.id);
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19 4H15.5L14.5 3H9.5L8.5 4H5V6H19M6 19C6 20.1 6.9 21 8 21H16C17.1 21 18 20.1 18 19V7H6V19Z" />
                  </svg>
                </button>
                <div className="overview-ad-edit-overlay">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
                  </svg>
                  <span>Click to edit</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>


      {/* Discard Changes Confirmation Dialog */}
      {showDiscardConfirm && (
        <div className="unsaved-changes-overlay" onClick={cancelDiscard}>
          <div className="unsaved-changes-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="unsaved-changes-header">
              <div className="unsaved-changes-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                </svg>
              </div>
              <h2>Unsaved Changes</h2>
            </div>
            <div className="unsaved-changes-body">
              <p className="unsaved-changes-message">
                You have unsaved changes. Are you sure you want to discard them?
              </p>
            </div>
            <div className="unsaved-changes-actions">
              <button className="unsaved-changes-cancel-btn" onClick={cancelDiscard}>
                Cancel
              </button>
              <button className="unsaved-changes-discard-btn" onClick={confirmDiscard}>
                Discard Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Ad Modal */}
      {showAddAdModal && (
        <div className="overview-modal-overlay" onClick={() => handleAdOverlayClick(() => setShowAddAdModal(false))}>
          <div className="overview-add-ad-modal" onClick={(e) => e.stopPropagation()}>
            <div className="overview-add-ad-header">
              <h2>Add New Advertisement</h2>
              <button
                className="overview-close-btn"
                onClick={() => handleAdOverlayClick(() => setShowAddAdModal(false))}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z" />
                </svg>
              </button>
            </div>

            <div className="overview-add-ad-content">
              <div className="overview-form-group">
                <label>Title (For Provider Page Hero)</label>
                <input
                  type="text"
                  placeholder="e.g., Access your account immediately and start learning now!"
                  value={newAd.title}
                  onChange={(e) => setNewAd({ ...newAd, title: e.target.value })}
                />
              </div>

              <div className="overview-form-group">
                <label>Subtitle (For Provider Page Hero)</label>
                <input
                  type="text"
                  placeholder="e.g., Direct Access to available providers"
                  value={newAd.subtitle}
                  onChange={(e) => setNewAd({ ...newAd, subtitle: e.target.value })}
                />
              </div>

              <div className="overview-form-group">
                <label>Button Text (For Provider Page Hero)</label>
                <input
                  type="text"
                  placeholder="e.g., Join Now"
                  value={newAd.button_text}
                  onChange={(e) => setNewAd({ ...newAd, button_text: e.target.value })}
                />
              </div>

              <div className="overview-form-group">
                <label>Description</label>
                <input
                  type="text"
                  placeholder="Enter ad description"
                  value={newAd.description}
                  onChange={(e) => setNewAd({ ...newAd, description: e.target.value })}
                />
              </div>

              <div className="overview-form-group">
                <label>Action URL</label>
                <input
                  type="url"
                  placeholder="https://example.com"
                  value={newAd.action_url}
                  onChange={(e) => {
                    setNewAd({ ...newAd, action_url: e.target.value });
                    // Clear error when user starts typing
                    if (actionUrlError) setActionUrlError('');
                  }}
                  style={{ borderColor: actionUrlError ? '#dc2626' : '' }}
                />
                {actionUrlError && (
                  <p style={{ color: '#dc2626', fontSize: '0.875rem', marginTop: '0.25rem', marginBottom: 0 }}>
                    {actionUrlError}
                  </p>
                )}
              </div>

              <div className="overview-form-group">
                <label>Image</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  disabled={uploadingAd}
                />
              </div>
            </div>

            <div className="overview-add-ad-actions">
              <button
                className="overview-cancel-btn"
                onClick={() => handleAdOverlayClick(() => setShowAddAdModal(false))}
              >
                Cancel
              </button>
              <p className="overview-upload-hint">Select an image to upload the ad</p>
            </div>
          </div>
        </div>
      )}
      {/* Edit Ad Modal */}
      {showEditAdModal && editingAd && (
        <div className="overview-modal-overlay" onClick={() => handleAdOverlayClick(() => setShowEditAdModal(false))}>
          <div className="overview-edit-ad-modal" onClick={(e) => e.stopPropagation()}>
            <div className="overview-add-ad-header">
              <h2>Edit Advertisement</h2>
              <button
                className="overview-close-btn"
                onClick={() => handleAdOverlayClick(() => setShowEditAdModal(false))}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z" />
                </svg>
              </button>
            </div>

            <div className="overview-edit-preview">
              <div className="overview-ad-image large">
                <img src={editingAd.image_url} alt={editingAd.description || 'Advertisement'} />
              </div>
            </div>

            <div className="overview-add-ad-content">
              <div className="overview-form-group">
                <label>Title (For Provider Page Hero)</label>
                <input
                  type="text"
                  placeholder="e.g., Access your account immediately and start learning now!"
                  value={newAd.title}
                  onChange={(e) => setNewAd({ ...newAd, title: e.target.value })}
                />
              </div>

              <div className="overview-form-group">
                <label>Subtitle (For Provider Page Hero)</label>
                <input
                  type="text"
                  placeholder="e.g., Direct Access to available providers"
                  value={newAd.subtitle}
                  onChange={(e) => setNewAd({ ...newAd, subtitle: e.target.value })}
                />
              </div>

              <div className="overview-form-group">
                <label>Button Text (For Provider Page Hero)</label>
                <input
                  type="text"
                  placeholder="e.g., Join Now"
                  value={newAd.button_text}
                  onChange={(e) => setNewAd({ ...newAd, button_text: e.target.value })}
                />
              </div>

              <div className="overview-form-group">
                <label>Description</label>
                <input
                  type="text"
                  placeholder="Enter ad description"
                  value={newAd.description}
                  onChange={(e) => setNewAd({ ...newAd, description: e.target.value })}
                />
              </div>

              <div className="overview-form-group">
                <label>Action URL</label>
                <input
                  type="url"
                  placeholder="https://example.com"
                  value={newAd.action_url}
                  onChange={(e) => {
                    setNewAd({ ...newAd, action_url: e.target.value });
                    // Clear error when user starts typing
                    if (actionUrlError) setActionUrlError('');
                  }}
                  style={{ borderColor: actionUrlError ? '#dc2626' : '' }}
                />
                {actionUrlError && (
                  <p style={{ color: '#dc2626', fontSize: '0.875rem', marginTop: '0.25rem', marginBottom: 0 }}>
                    {actionUrlError}
                  </p>
                )}
              </div>

              <div className="overview-form-group">
                <label>Replace Image (Optional)</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={async (event) => {
                    const file = event.target.files?.[0];
                    if (!file) return;
                    try {
                      setUploadingAd(true);
                      const ext = file.name.split('.').pop();
                      const fileName = `${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
                      const path = `ads/${fileName}`;
                      const { error: upErr } = await supabase.storage.from('assets').upload(path, file);
                      if (upErr) { alert(`Upload error: ${upErr.message}`); setUploadingAd(false); return; }

                      const { data: { publicUrl } } = supabase.storage.from('assets').getPublicUrl(path);
                      // update preview immediately
                      setEditingAd((prev) => prev ? { ...prev, image_url: publicUrl } : prev);
                      // persist image change
                      await supabase.from('ads').update({ image_url: publicUrl }).eq('id', editingAd.id);
                    } catch (e) {
                      alert(`Error replacing image: ${e.message}`);
                    } finally {
                      setUploadingAd(false);
                    }
                  }}
                  disabled={uploadingAd}
                />
              </div>
            </div>

            <div className="overview-add-ad-actions">
              <button
                className="overview-delete-ad-action-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  if (editingAd) {
                    setShowEditAdModal(false);
                    handleDeleteAd(editingAd.id);
                  }
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 4H15.5L14.5 3H9.5L8.5 4H5V6H19M6 19C6 20.1 6.9 21 8 21H16C17.1 21 18 20.1 18 19V7H6V19Z" />
                </svg>
                Delete
              </button>
              <div style={{ flex: 1 }}></div>
              <button className="overview-cancel-btn" onClick={() => handleAdOverlayClick(() => setShowEditAdModal(false))}>
                Cancel
              </button>
              <button className="overview-confirm-btn" onClick={handleUpdateAd} disabled={uploadingAd}>
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}



      {/* Delete Single Activity Confirmation Modal */}
      {showDeleteItemConfirm && (
        <div className="overview-modal-overlay" onClick={() => {
          setShowDeleteItemConfirm(false);
          setDeleteItemId(null);
        }}>
          <div className="overview-confirmation-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="overview-confirmation-header">
              <div className="overview-confirmation-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 4H15.5L14.5 3H9.5L8.5 4H5V6H19M6 19C6 20.1 6.9 21 8 21H16C17.1 21 18 20.1 18 19V7H6V19Z" />
                </svg>
              </div>
              <h2>Delete Activity</h2>
            </div>

            <div className="overview-confirmation-content">
              <p className="overview-confirmation-message">
                Are you sure you want to delete this activity?
              </p>
              <p className="overview-confirmation-warning">
                This action cannot be undone.
              </p>
            </div>

            <div className="overview-confirmation-actions">
              <button className="overview-cancel-btn" onClick={() => {
                setShowDeleteItemConfirm(false);
                setDeleteItemId(null);
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z" />
                </svg>
                Cancel
              </button>
              <button className="overview-confirm-btn" onClick={() => handleDeleteActivity(deleteItemId)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 4H15.5L14.5 3H9.5L8.5 4H5V6H19M6 19C6 20.1 6.9 21 8 21H16C17.1 21 18 20.1 18 19V7H6V19Z" />
                </svg>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Ad Confirmation Modal */}
      {showDeleteAdModal && (
        <div className="overview-modal-overlay" onClick={() => {
          setShowDeleteAdModal(false);
          setDeleteAdId(null);
        }}>
          <div className="overview-delete-ad-modal" onClick={(e) => e.stopPropagation()}>
            <div className="overview-delete-ad-header">
              <div className="overview-delete-ad-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 4H15.5L14.5 3H9.5L8.5 4H5V6H19M6 19C6 20.1 6.9 21 8 21H16C17.1 21 18 20.1 18 19V7H6V19Z" />
                </svg>
              </div>
              <h2>Delete Advertisement</h2>
            </div>

            <div className="overview-delete-ad-content">
              <p className="overview-delete-ad-message">
                Are you sure you want to delete this advertisement?
              </p>
              <p className="overview-delete-ad-warning">
                This action cannot be undone and will permanently remove the ad from the system.
              </p>
            </div>

            <div className="overview-delete-ad-actions">
              <button className="overview-cancel-btn" onClick={() => {
                setShowDeleteAdModal(false);
                setDeleteAdId(null);
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z" />
                </svg>
                Cancel
              </button>
              <button className="overview-delete-ad-confirm-btn" onClick={confirmDeleteAd}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 4H15.5L14.5 3H9.5L8.5 4H5V6H19M6 19C6 20.1 6.9 21 8 21H16C17.1 21 18 20.1 18 19V7H6V19Z" />
                </svg>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardOverview;



